"use client";

import {
  ctaClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

export interface Enrollments1Data {
  id?: string;
  status?: boolean;
  headline?: string;
  cta_label?: string;
  cta_url?: string;
  open_in_new_tab?: boolean;
  outer_background_color?: string;
  inner_background_color?: string;
  /** Optional background image for the inner card — overrides `inner_background_color`. Shares variant 3's columns. */
  desktop_image?: string;
  mobile_image?: string;
  headline_color?: string;
  cta_background_color?: string;
  cta_text_color?: string;
  /** Shares `enrollments.enable_lead_popup` with variant 2 */
  enable_lead_popup?: boolean;
}

interface Enrollments1Props {
  data: Enrollments1Data | { data: Enrollments1Data };
}

function resolvePayload(data: Enrollments1Props["data"]): Enrollments1Data | undefined {
  const anyData = data as { data?: Enrollments1Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as Enrollments1Data;
  }
  return data as Enrollments1Data;
}

const Enrollments1 = ({ data }: Enrollments1Props) => {
  const dispatch = useDispatch();
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);

  const {
    status = true,
    headline = "Ready to Take the Next Step? Enroll Today!",
    cta_label = "Apply Now",
    cta_url = "",
    open_in_new_tab = true,
    outer_background_color = "#FDE1D1",
    inner_background_color = "#DB5803",
    desktop_image = "",
    mobile_image = "",
    headline_color = "#FFFFFF",
    cta_background_color = "#FFFFFF",
    cta_text_color = "#DB5803",
    enable_lead_popup: enableLeadRaw,
  } = sectionData || {};

  const raw = sectionData as Record<string, unknown> | undefined;
  const enableLeadPopup = isCmsShowFlagOn(
    raw?.enable_lead_popup ?? raw?.enableLeadPopup ?? enableLeadRaw,
    true,
  );

  const hrefTrim = (cta_url || "").trim();
  const ctaOpensLeadPopup = enableLeadPopup || !hrefTrim;

  const desktopBgImage = (desktop_image || "").trim();
  const mobileBgImage = (mobile_image || "").trim() || desktopBgImage;

  const openLeadFromCta = useCallback(() => {
    ctaClick({
      section: "enrollments_1",
      component: "cta_apply",
      label: cta_label?.trim() || "apply",
      step: "1",
      entity: "enrollments",
      parentEntity: "enrollments_1",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [cta_label, dispatch]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status) {
  //     FireWidgetViewedEvent(ref, "enrollments_1");
  //   }
  // }, [status]);

  if (!status) return null;

  const ctaClassName = cn(
    "inline-flex shrink-0 items-center gap-2 rounded-md font-semibold shadow-sm transition-opacity",
    "px-3 py-2 text-sm leading-snug hover:opacity-95",
    "min-[768px]:gap-2.5 min-[768px]:px-[18px] min-[768px]:py-3 min-[768px]:text-base min-[768px]:leading-[23px]",
    ctaOpensLeadPopup && "cursor-pointer",
    !ctaOpensLeadPopup && !hrefTrim && "cursor-default opacity-90",
  );

  const ctaIconClass = "size-5 shrink-0 min-[768px]:size-6";

  const ctaStyle = {
    backgroundColor: cta_background_color,
    color: cta_text_color,
  };

  const renderCta = () => {
    if (ctaOpensLeadPopup) {
      return (
        <button
          type="button"
          className={ctaClassName}
          style={ctaStyle}
          onClick={openLeadFromCta}
        >
          {cta_label}
          <ArrowUpRight className={ctaIconClass} strokeWidth={2} aria-hidden />
        </button>
      );
    }
    if (hrefTrim) {
      return (
        <a
          href={hrefTrim}
          target={open_in_new_tab ? "_blank" : undefined}
          rel={open_in_new_tab ? "noopener noreferrer" : undefined}
          className={ctaClassName}
          style={ctaStyle}
          onClick={() =>
            ctaClick({
              section: "enrollments_1",
              component: "cta_apply_link",
              label: cta_label?.trim() || "apply",
              step: "1",
              entity: "enrollments",
              parentEntity: "enrollments_1",
            })
          }
        >
          {cta_label}
          <ArrowUpRight className={ctaIconClass} strokeWidth={2} aria-hidden />
        </a>
      );
    }
    return (
      <span className={cn(ctaClassName, "cursor-default opacity-90")} style={ctaStyle}>
        {cta_label}
        <ArrowUpRight className={ctaIconClass} strokeWidth={2} aria-hidden />
      </span>
    );
  };

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative flex w-full items-center justify-center overflow-hidden",
        "py-8 px-4",
        "min-[768px]:h-[362px] min-[768px]:py-0 min-[768px]:px-spacing-32",
      )}
      style={{
        backgroundColor: desktopBgImage || mobileBgImage ? "transparent" : "#FFFFFF",
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {/* Section background image (optional) — sits behind the card, card stays unchanged */}
      {mobileBgImage && (
        <Image
          src={mobileBgImage}
          alt=""
          aria-hidden
          fill
          sizes="100vw"
          className="absolute inset-0 z-0 object-cover min-[768px]:hidden"
        />
      )}
      {desktopBgImage && (
        <Image
          src={desktopBgImage}
          alt=""
          aria-hidden
          fill
          sizes="100vw"
          className="absolute inset-0 z-0 hidden object-cover min-[768px]:block"
        />
      )}

      {/* Mobile (Figma 255:12917): single orange surface, ~12.35px radius, 292×216 */}
      <div
        className={cn(
          "relative z-[1] flex min-[768px]:hidden",
          "h-[216px] w-[min(292px,calc(100vw-2rem))] max-w-[292px]",
          "flex-col overflow-hidden rounded-[12.35px] px-4 py-5",
        )}
        style={{ backgroundColor: inner_background_color }}
      >
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3">
          <p
            className="w-full max-w-[260px] text-center text-[20px] font-semibold leading-[1.26] tracking-[-0.02em]"
            style={{ color: headline_color }}
          >
            {headline}
          </p>
          {renderCta()}
        </div>
      </div>

      {/* Desktop: peach frame 634×282 (12px frame) + inner card 610×258 */}
      <div
        className={cn(
          "relative z-[1] hidden min-[768px]:flex",
          "box-border h-[282px] w-[634px] flex-col overflow-hidden rounded-[30px] p-[12px]",
        )}
        style={{ backgroundColor: outer_background_color }}
      >
        <div
          className="flex min-h-0 flex-1 flex-col items-center justify-center gap-[31px] overflow-hidden rounded-2xl px-8 py-6"
          style={{ backgroundColor: inner_background_color }}
        >
          <p
            className="max-w-[530px] text-center text-[26px] font-semibold leading-[1.26] tracking-[-0.03em]"
            style={{ color: headline_color }}
          >
            {headline}
          </p>
          {renderCta()}
        </div>
      </div>
    </section>
  );
};

export default Enrollments1;
